import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import NavGlyph from '../common/NavGlyph';
import { NAV_SECTIONS, ADMIN_NAV_SECTIONS, IS_DEV, ROUTES } from '../../constants/constants';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { useCompanyDeadlines } from '../../hooks/useCompanyDeadlines';
import { isSddApplicable } from '../../utils/companyListing';
import { USER_ROLES } from '../../constants/constants';
import { RiUser3Fill, RiLogoutBoxRFill } from 'react-icons/ri';

function NavBadge({ children, isActive }) {
  const style = {
    background: isActive ? '#475569' : '#334155',
    color: '#ffffff',
    border: 'none',
    transition: 'all 0.2s ease-in-out',
  };

  return (
    <span
      className="ml-auto text-[10px] font-semibold px-2 py-[2.5px] rounded-full transition-all duration-200 leading-none"
      style={style}
    >
      {children}
    </span>
  );
}



function getInitials(fullName) {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'PS';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export default function Sidebar({ hasDashboardAlert = false, open = false, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { activeCompany, companies, selectCompany, clearCompany, noCompanyMode, openAddCompanyModal } = useApp();
  const { alerts, stats } = useCompanyDeadlines();

  const [showSwitcher, setShowSwitcher] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Filters the menu itself (not the company list above, which has its own box).
  const [navQuery, setNavQuery] = useState('');
  const navFilter = navQuery.trim().toLowerCase();

  // Collapsible nav sections — persisted across navigation like scroll position
  const COLLAPSED_BY_DEFAULT = ['Company Types', 'Business Platform'];
  const [openSections, setOpenSections] = useState(() => {
    try {
      const saved = sessionStorage.getItem('sidebar_open_sections');
      if (saved) return JSON.parse(saved);
    } catch (_) { /* ignore */ }
    return {};
  });
  const toggleSection = (label) => {
    setOpenSections((prev) => {
      const current = prev[label] ?? !COLLAPSED_BY_DEFAULT.includes(label);
      const next = { ...prev, [label]: !current };
      try { sessionStorage.setItem('sidebar_open_sections', JSON.stringify(next)); } catch (_) { /* ignore */ }
      return next;
    });
  };

  const navRef = useRef(null);
  const profileMenuRef = useRef(null);

  // Restore scroll position
  useLayoutEffect(() => {
    if (navRef.current) {
      const saved = sessionStorage.getItem('sidebar_scroll');
      if (saved) {
        navRef.current.scrollTop = parseInt(saved, 10);
      }
    }
  }, []);

  const handleScroll = (e) => {
    sessionStorage.setItem('sidebar_scroll', e.currentTarget.scrollTop.toString());
  };

  // Close profile menu on navigation
  useEffect(() => {
    setShowProfileMenu(false);
  }, [location.pathname]);

  // Handle profile dropup click outside & Esc key
  useEffect(() => {
    if (!showProfileMenu) return;
    const handleOutsideClick = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setShowProfileMenu(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showProfileMenu]);

  const handleSignOut = async () => {
    await logout();
    navigate(ROUTES.LOGIN, { replace: true });
  };

  const badgeCounts = {
    totalCompanies: stats.total,
    criticalAlerts: alerts.filter((a) => (a.daysRemaining ?? 0) < 0).length,
  };

  const rawRole = String(user?.roleName || '').toLowerCase();
  const isAdmin = ['admin', 'super_admin'].includes(rawRole);
  const effectiveRoles = [rawRole];
  if (isAdmin) {
    // Admins get the user-facing viewing roles for shared nav, but only
    // their REAL admin tier — a plain admin must not see super_admin-only
    // items (Flag Inbox, Admin Alerts, Governance).
    effectiveRoles.push('professional', 'corporate');
  }
  if (!effectiveRoles.includes(USER_ROLES.PROFESSIONAL) && !effectiveRoles.includes(USER_ROLES.CORPORATE)) {
    effectiveRoles.push(rawRole.includes('corp') ? USER_ROLES.CORPORATE : USER_ROLES.PROFESSIONAL);
  }
  const displayName = String(user?.fullName || user?.name || '').trim() || 'Professional User';
  const userInitials = getInitials(displayName);

  const roleConfig = {
    label: isAdmin ? 'ADMIN' : rawRole === USER_ROLES.PROFESSIONAL ? 'CA/CS PRO' : 'CORPORATE',
    bg: 'bg-white/[0.05] group-hover:bg-white/[0.09]',
    text: 'text-white font-semibold',
    mutedText: 'text-white/60 group-hover:text-white/80'
  };

  return (
    <aside
      className={`fixed left-0 top-0 h-[var(--app-vh)] w-[260px] flex flex-col z-50 overflow-hidden transition-transform duration-300 ease-in-out lg:translate-x-0 ${open ? 'translate-x-0' : '-translate-x-full'}`}
      style={{
        background: 'linear-gradient(to bottom, #0c1727, #08111f, #040912)',
        boxShadow: '4px 0 40px rgba(0,0,0,0.25)',
        fontFamily: "'Outfit', sans-serif"
      }}
    >
      {/* Unified Brand Header & Workspace Switcher */}
      <div className="px-4 py-4 shrink-0 relative overflow-hidden">
        {/* Soft radial glow behind the header */}
        <div
          className="absolute -top-12 -left-12 w-28 h-28 rounded-full blur-[40px] pointer-events-none opacity-20"
          style={{ background: 'radial-gradient(circle, #b8973a 0%, transparent 70%)' }}
        />

        {isAdmin ? (
          // Admin Console Brand Header (Static)
          <div className="flex items-center gap-3 relative z-10 px-2 py-1.5">
            <div className="relative flex-shrink-0">
              <div className="absolute inset-0 rounded-full blur-[6px] bg-[#b8973a]/20 animate-pulse" />
              <img
                src="/complyv-logo.svg"
                alt="ComplyV"
                className="w-9 h-9 relative z-10 object-contain"
              />
            </div>
            <div className="min-w-0 flex-1">
              <div
                className="text-[17px] font-bold leading-none tracking-[0.2px] hover:text-[#e5c26b] transition-colors cursor-pointer"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: '#b8973a' }}
                onClick={() => navigate('/admin')}
              >
                ComplyV
              </div>
              <div className="text-[9px] text-white/60 tracking-[2.5px] uppercase mt-1.5 leading-tight font-semibold">
                Admin Console
              </div>
            </div>
            {/* Close menu for mobile */}
            <button
              onClick={onClose}
              className="lg:hidden flex-shrink-0 p-1.5 rounded-md text-white/55 hover:text-white hover:bg-white/10 transition-colors cursor-pointer focus:outline-none"
              aria-label="Close menu"
            >
              <span className="text-[15px] leading-none">✕</span>
            </button>
          </div>
        ) : (
          // Unified Workspace Switcher (Interactive)
          <div className="flex items-center gap-2 relative z-10">
            <div
              role="button"
              tabIndex={0}
              onClick={() => setShowSwitcher(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowSwitcher(true);
                }
              }}
              className="flex-1 flex items-center gap-3 p-2.5 rounded-xl text-left bg-white/[0.03] hover:bg-white/[0.06] active:scale-[0.98] transition-all duration-200 cursor-pointer group outline-none focus:outline-none min-w-0"
              style={{ outline: 'none', boxShadow: 'none' }}
            >
              {/* Overlapping Avatars Combo */}
              <div className="relative w-8 h-8 flex-shrink-0">
                {/* Initials Avatar for Active Company */}
                <div className="w-full h-full rounded-lg flex items-center justify-center text-[11px] font-bold text-[#08111f] relative z-10 bg-gradient-to-br from-[#b8973a] to-[#d4b05a] shadow-md shadow-black/30 group-hover:scale-105 transition-transform duration-300">
                  {activeCompany ? getInitials(activeCompany.name) : noCompanyMode ? '—' : 'CS'}
                </div>
                {/* Float Mini ComplyV Badge */}
                <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-[#08111f] border-[1px] border-white/15 flex items-center justify-center z-20 shadow-lg">
                  <img
                    src="/complyv-logo.svg"
                    alt=""
                    className="w-2.5 h-2.5 object-contain"
                  />
                </div>
              </div>

              <div className="min-w-0 flex-1">
                <div className="text-[9px] font-bold tracking-[2px] uppercase text-[#e5c26b]/70 group-hover:text-[#e5c26b] transition-colors leading-none">
                  ComplyV
                </div>
                <div className="text-[12px] font-bold text-white/90 truncate mt-1 group-hover:text-white group-active:translate-y-[0.5px] transition-all duration-150 leading-tight">
                  {activeCompany ? activeCompany.name : noCompanyMode ? 'No company — general' : 'Select Company'}
                </div>
              </div>
            </div>

            {/* Close menu for mobile */}
            <button
              onClick={onClose}
              className="lg:hidden flex-shrink-0 p-1.5 rounded-md text-white/55 hover:text-white hover:bg-white/10 transition-colors cursor-pointer focus:outline-none"
              aria-label="Close menu"
            >
              <span className="text-[15px] leading-none">✕</span>
            </button>
          </div>
        )}
      </div>

      {/* Switcher slide-up overlay panel */}
      {showSwitcher && (
        <div className="absolute inset-0 bg-[#08111f]/95 backdrop-blur-md z-50 flex flex-col p-4 animate-in slide-in-from-bottom duration-300">
          <div className="flex items-center justify-between mb-5">
            <span
              className="text-[9.5px] font-bold tracking-[2.2px] uppercase text-white/70"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Select Company
            </span>
            <button
              onClick={() => {
                setShowSwitcher(false);
                setSearchQuery('');
              }}
              className="flex items-center justify-center w-8 h-8 rounded-lg text-white/60 hover:text-white hover:bg-white/[0.08] transition-all duration-200 active:scale-95 focus:outline-none"
              aria-label="Back"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          </div>

          {/* Search Input */}
          <div className="mb-4 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-4 w-4 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search company..."
              className="w-full bg-white/[0.03] rounded-xl pl-9 pr-4 py-2.5 text-[12.5px] text-white placeholder-white/45 outline-none focus:bg-white/[0.06] transition-all duration-200"
            />
          </div>

          {/* Company List */}
          <div
            className="flex-1 min-h-0 overflow-y-auto space-y-1.5 cs-sidebar-scroll"
            style={{ scrollbarGutter: 'auto' }}
          >
            {/* Work with no company: ask Vee general questions, browse the rule
                book, draft with placeholders — nothing tied to a company. */}
            {!searchQuery && (
              <button
                onClick={() => {
                  clearCompany();
                  setShowSwitcher(false);
                }}
                className={`w-full text-left p-3 rounded-xl transition-all duration-200 flex items-center justify-between gap-3 active:scale-[0.98] ${noCompanyMode
                    ? 'bg-white/[0.06] shadow-lg shadow-black/20'
                    : 'bg-transparent hover:bg-white/[0.03] text-white/80 hover:text-white'
                  }`}
              >
                <div className="min-w-0 flex-1">
                  <div className={`font-semibold text-[13px] truncate ${noCompanyMode ? 'text-white' : 'text-white/90'}`}>No company — general questions</div>
                  <div className={`text-[10.5px] mt-0.5 truncate ${noCompanyMode ? 'text-[#e5c26b]/80' : 'text-white/50'}`}>Ask Vee anything; nothing is tied to a company</div>
                </div>
                {noCompanyMode && (
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[#b8973a]/25 flex items-center justify-center">
                    <svg className="w-3 h-3 text-[#e5c26b]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </button>
            )}
            {companies
              .filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()))
              .map((company) => {
                const isSelected = activeCompany?.cin === company.cin;
                return (
                  <button
                    key={company.cin}
                    onClick={() => {
                      selectCompany(company);
                      setShowSwitcher(false);
                      setSearchQuery('');
                    }}
                    className={`w-full text-left p-3 rounded-xl transition-all duration-200 flex items-center justify-between gap-3 active:scale-[0.98] ${isSelected
                        ? 'bg-white/[0.06] shadow-lg shadow-black/20'
                        : 'bg-transparent hover:bg-white/[0.03] text-white/80 hover:text-white'
                      }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className={`font-semibold text-[13px] truncate ${isSelected ? 'text-white' : 'text-white/90'}`}>{company.name}</div>
                      <div className={`text-[10.5px] mt-0.5 truncate ${isSelected ? 'text-[#e5c26b]/80' : 'text-white/50'}`}>{company.cin}</div>
                    </div>
                    {isSelected && (
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[#b8973a]/25 flex items-center justify-center">
                        <svg className="w-3 h-3 text-[#e5c26b]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            {companies.length === 0 && (
              <div className="text-center text-white/50 py-6 text-[12px]">No companies found</div>
            )}
          </div>

          {/* Add New Company Button */}
          <button
            onClick={() => {
              setShowSwitcher(false);
              setSearchQuery('');
              openAddCompanyModal();
            }}
            className="w-full mt-4 flex items-center justify-center gap-2 p-3 rounded-xl bg-gradient-to-r from-[#b8973a] to-[#d4b05a] hover:from-[#d4b05a] hover:to-[#e5c26b] text-[#08111f] font-bold text-[12.5px] text-center transition-all duration-300 shadow-md shadow-black/20 hover:shadow-lg active:scale-[0.98]"
          >
            <svg className="w-4 h-4 text-[#08111f]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            <span>Add New Company</span>
          </button>
        </div>
      )}

      {/* Menu search — finds a page by name without scrolling the menu */}
      <div className="px-4 pt-3 pb-1 relative z-10 shrink-0">
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/45 pointer-events-none"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2} aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 10.5a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" />
          </svg>
          <input
            type="text"
            value={navQuery}
            onChange={(e) => setNavQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setNavQuery(''); }}
            placeholder="Search menu…"
            aria-label="Search menu"
            autoComplete="off"
            className="w-full h-[32px] pl-8 pr-8 rounded-lg text-[12.5px] text-white placeholder-white/40 bg-white/[0.05] border border-white/[0.08] focus:outline-none focus:border-[#b8973a]/60 focus:bg-white/[0.07] transition-colors"
          />
          {navQuery && (
            <button
              type="button"
              onClick={() => setNavQuery('')}
              aria-label="Clear menu search"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded text-white/50 hover:text-white hover:bg-white/10"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav
        ref={navRef}
        onScroll={handleScroll}
        className="cs-sidebar-scroll flex-1 min-h-0 py-3 overflow-y-auto overflow-x-hidden relative z-10 space-y-4"
      >
        {(isAdmin ? ADMIN_NAV_SECTIONS : NAV_SECTIONS).filter((s) => !s.devOnly || IS_DEV).map((section) => {
          const allowedItems = section.items.filter((item) =>
            item.roles.some((r) => effectiveRoles.includes(r))
            && (!item.devOnly || IS_DEV)
            // `requiresSdd` items depend on WHICH company is selected, not on
            // who is logged in — so this re-evaluates as the user switches
            // company, unlike the role test above.
            && (!item.requiresSdd || isSddApplicable(activeCompany))
          );
          // While searching, a page matches by its name or its section's name
          // ("drafting" lists the whole Drafting Studio).
          const sectionMatches = !!navFilter && section.label.toLowerCase().includes(navFilter);
          const visibleItems = navFilter && !sectionMatches
            ? allowedItems.filter((item) => String(item.label || '').toLowerCase().includes(navFilter))
            : allowedItems;
          if (!visibleItems.length) return null;

          // Routes may carry a query string (e.g. /admin/users?filter=locked),
          // so match against pathname+search first, then bare pathname.
          const routeIsActive = (route) =>
            route === `${location.pathname}${location.search}` ||
            (!route.includes('?') && route === location.pathname);
          const sectionHasActive = visibleItems.some((it) => routeIsActive(it.route));
          const userState = openSections[section.label];
          // Searching opens every section that has a hit, so a match is never hidden.
          const isOpen = !!navFilter || sectionHasActive || (userState ?? !COLLAPSED_BY_DEFAULT.includes(section.label));

          return (
            <div key={section.label} className="space-y-1">
              {sectionHasActive ? (
                // Section holds the current page — locked open, so render a plain
                // label without a chevron (a toggle that can't collapse misleads).
                <div
                  className="w-full text-[10px] tracking-[1.6px] uppercase font-semibold px-6 pt-2 pb-1.5 flex items-center gap-2"
                  style={{ color: 'rgba(255, 255, 255, 0.68)' }}
                >
                  {section.label}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => toggleSection(section.label)}
                  aria-expanded={isOpen}
                  className="group/sec w-full text-[10px] tracking-[1.6px] uppercase font-semibold px-6 pt-2 pb-1.5 flex items-center gap-2 cursor-pointer focus:outline-none"
                  style={{ color: 'rgba(255, 255, 255, 0.68)' }}
                >
                  <span className="group-hover/sec:text-white/90 group-active/sec:translate-y-[0.5px] transition-all duration-150">{section.label}</span>
                  <svg
                    className={`ml-auto w-3 h-3 text-white/35 group-hover/sec:text-white/60 transition-all duration-200 ${isOpen ? '' : '-rotate-90'}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              )}
              {isOpen && visibleItems.map((item) => {
                const isActive = routeIsActive(item.route);
                const dynamicCount = item.badgeKey ? badgeCounts[item.badgeKey] : null;
                const displayBadge = dynamicCount ?? item.badge ?? null;

                return (
                  <button
                    key={item.id}
                    onClick={() => navigate(item.route)}
                    className={`group relative w-[calc(100%-24px)] mx-3 flex items-center gap-3 px-3 ${item.highlight ? 'h-[38px] text-[13.5px]' : 'h-[32px] text-[12.5px]'} rounded-lg transition-all duration-200 cursor-pointer active:scale-[0.98] ${item.highlight
                        ? `font-bold text-[#e5c26b] ring-1 ${isActive ? 'ring-[#b8973a]/60 bg-gradient-to-r from-[#b8973a]/30 to-[#b8973a]/[0.06]' : 'ring-[#b8973a]/30 bg-gradient-to-r from-[#b8973a]/[0.16] to-transparent hover:from-[#b8973a]/25'}`
                        : isActive
                          ? 'font-semibold text-[#e5c26b] bg-white/[0.06]'
                          : 'text-white/80 hover:text-white hover:bg-white/[0.04]'
                      }`}
                  >
                    <span className={`w-[17px] flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-hover:translate-x-0.5 ${isActive ? 'text-[#e5c26b]' : 'text-white/70 group-hover:text-white'}`}>
                      <NavGlyph icon={item.icon} size={17} />
                    </span>
                    <span className="min-w-0 flex-1 leading-[1.15] text-left transition-transform duration-200 group-hover:translate-x-0.5 group-active:translate-y-[0.5px]">{item.label}</span>
                    {item.tag && (
                      <span className="ml-auto text-[9px] font-bold tracking-wide px-1.5 py-[2px] rounded bg-[#b8973a] text-[#08111f] leading-none">{item.tag}</span>
                    )}
                    {displayBadge !== null && (
                      <NavBadge isActive={isActive}>{displayBadge}</NavBadge>
                    )}

                  </button>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Interactive Profile Footer with Dropup Menu */}
      <div
        ref={profileMenuRef}
        className="w-full px-4 py-4 mt-auto shrink-0 z-20"
      >
        <div className="relative w-full">
          {/* Profile Dropup Menu */}
          {showProfileMenu && (
            <div
              className="absolute bottom-full left-0 right-0 mb-1.5 rounded-xl p-1.5 z-[60] flex flex-col gap-1"
              style={{
                background: 'rgba(8, 17, 31, 0.97)',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 -10px 40px -10px rgba(0,0,0,0.6), 0 0 20px rgba(0,0,0,0.3)',
                border: '1px solid rgba(255, 255, 255, 0.05)',
              }}
            >
              {/* User Info Header */}
              <div className="px-3 py-2 flex flex-col min-w-0">
                <div className="text-[13px] font-bold truncate text-white">{displayName}</div>
                <div className="text-[12px] truncate mt-0.5 text-white/60">{user?.email || 'No email provided'}</div>
              </div>
              <div className="h-[1px] bg-white/5" />

              {/* Menu Items */}
              <button
                onClick={() => {
                  setShowProfileMenu(false);
                  navigate(ROUTES.PROFILE);
                }}
                className="w-full flex items-center gap-2.5 px-3 h-[32px] text-[12.5px] font-semibold rounded-lg text-left cursor-pointer text-white/80 hover:text-white hover:bg-white/5 border-none outline-none transition-all duration-200 active:scale-[0.98]"
                style={{ border: 'none', outline: 'none' }}
              >
                <RiUser3Fill className="w-3.5 h-3.5 text-white/60" />
                My Profile
              </button>

              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-2.5 px-3 h-[32px] text-[12.5px] font-semibold rounded-lg text-left cursor-pointer text-[#e57373] hover:text-[#ff8a80] hover:bg-red-500/10 border-none outline-none transition-all duration-200 active:scale-[0.98]"
                style={{ border: 'none', outline: 'none' }}
              >
                <RiLogoutBoxRFill className="w-3.5 h-3.5 text-[#e57373]" />
                Sign Out
              </button>
            </div>
          )}

          {/* Profile Trigger Button Card */}
          <button
            onClick={() => setShowProfileMenu((prev) => !prev)}
            className={`group w-full flex items-center gap-3 p-2.5 rounded-xl cursor-pointer text-left border-none outline-none active:scale-[0.98] transition-all duration-200 min-w-0 ${showProfileMenu
                ? 'bg-white/[0.08]'
                : 'bg-white/[0.03] hover:bg-white/[0.06]'
              }`}
            style={{ border: 'none', outline: 'none', boxShadow: 'none' }}
          >
            {/* Avatar Frame - styled consistent with company component but neutral slate */}
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-extrabold flex-shrink-0 ${roleConfig.bg} ${roleConfig.text} shadow-md shadow-black/15 group-hover:scale-105 transition-transform duration-300`}
            >
              {userInitials}
            </div>

            <div className="min-w-0 flex-1">
              <div className={`text-[9px] font-bold tracking-[2px] uppercase ${roleConfig.mutedText} transition-colors leading-none`}>
                {roleConfig.label}
              </div>
              <div
                className="text-[12px] font-bold text-white/90 truncate mt-1 group-hover:text-white group-active:translate-y-[0.5px] transition-all duration-150 leading-tight"
                title={displayName}
              >
                {displayName}
              </div>
            </div>
          </button>
        </div>
      </div>
    </aside>
  );
}
